/**
 * The plan, laid out as a DAG (docs/JOB-LIFECYCLE.md 2.8).
 *
 * A fold over the rows the hub already sends, and nothing else: no graph
 * library, no measurement pass, no layout that has to run in a browser. Twenty
 * nodes is the realistic ceiling for one job, so the whole thing is a couple of
 * linear passes and the arithmetic below.
 *
 * Layering is by **longest path from the roots**, which is the one rule that
 * makes an edge always point rightwards: a task sits one column past the
 * furthest of the things it waits on, so `A -> B -> C` with `A -> C` as well
 * puts C in column 2 rather than column 1, and the direct edge spans two
 * columns instead of pointing backwards. Within a column the order is the
 * task key, naturally, so `T2` comes before `T10`.
 *
 * This module has no imports on purpose: `packages/hub/test/graph.test.ts`
 * exercises it directly, and the geometry is the contract the renderer and the
 * test both read.
 */

/** Everything the layout needs from a task. A `Task` satisfies it as it is. */
export interface GraphNodeInput {
  key: string;
  dependsOn: readonly string[];
}

export interface PlacedNode {
  key: string;
  /** The longest path from a root, which is the column this node sits in. */
  depth: number;
  /** Its position inside that column, counted from the top of the column. */
  row: number;
  x: number;
  y: number;
}

export interface PlacedEdge {
  /** The dependency, drawn on the left. */
  from: string;
  /** The task that waits on it, drawn on the right. */
  to: string;
}

export interface GraphLayout {
  /** Every node, in reading order: column by column, and by key inside one. */
  nodes: PlacedNode[];
  /** Every edge whose two ends are both in `nodes`, deduplicated. */
  edges: PlacedEdge[];
  columns: number;
  /** The tallest column, in nodes. */
  rows: number;
  /** The canvas the nodes need, in px. Zero on an empty graph. */
  width: number;
  height: number;
}

/**
 * Node geometry, in px. The renderer positions from these and nothing else.
 *
 * The card is a fixed box on purpose. A layout that measured its nodes would
 * have to run in a browser, could not be folded in a test, and would reflow
 * every edge whenever a title wrapped differently; a fixed box costs a
 * two-line clamp on the title and buys a layout that is pure arithmetic.
 * The height is what four rows of node content need: the key line, two lines
 * of title, the repo line and the state line.
 *
 * The box grew when the graph moved onto the job's own page. It was sized for a
 * route of its own that a person reached and then left, at 200x116 with the
 * title at 12px clamped to two lines, which is about four words: on a real plan
 * every second node read "Wire the approval gate into…" and the picture told you
 * the shape of the job without telling you what any of it was. The page has the
 * room, so the title gets 13px over two lines in 240px, which is around nine
 * words, and the gutters grow with it so a five-column graph does not close up.
 */
export const NODE_WIDTH = 240;
export const NODE_HEIGHT = 124;
export const COLUMN_GAP = 64;
export const ROW_GAP = 16;

const COLUMN_PITCH = NODE_WIDTH + COLUMN_GAP;
const ROW_PITCH = NODE_HEIGHT + ROW_GAP;

/**
 * Task keys sort the way a person reads them: `T2` before `T10`, not after.
 *
 * A plain string sort puts `T10` between `T1` and `T2`, which on a column of
 * eleven nodes is eleven rows in an order nobody asked for. Digits compare as
 * numbers, everything else compares as text, case-insensitively, with the raw
 * string as the tie-break so the order is total.
 */
export function compareKeys(a: string, b: string): number {
  const chunks = /(\d+|\D+)/g;
  const left = a.match(chunks) ?? [];
  const right = b.match(chunks) ?? [];
  for (let index = 0; index < Math.min(left.length, right.length); index++) {
    const one = left[index]!;
    const two = right[index]!;
    const oneIsNumber = /^\d/.test(one);
    const twoIsNumber = /^\d/.test(two);
    if (oneIsNumber && twoIsNumber) {
      const difference = Number(one) - Number(two);
      if (difference !== 0) return difference;
      continue;
    }
    const difference = one.toLowerCase().localeCompare(two.toLowerCase());
    if (difference !== 0) return difference;
  }
  if (left.length !== right.length) return left.length - right.length;
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * The fold. Deterministic, total, and it never drops a node.
 *
 * Three kinds of input it has to survive, because the console draws whatever
 * the hub sends rather than whatever the schema promised:
 *
 *  - a `dependsOn` naming a key that is not in this list (an amendment can
 *    depend on a task the capped task list did not reach): the edge is dropped
 *    and the node is treated as a root, because half an edge is a line to
 *    nowhere.
 *  - the same key twice: the first row wins and the second is ignored, so two
 *    nodes never land on one square.
 *  - a cycle, which `topoOrder` in core rules out but this cannot verify: the
 *    nodes it cannot layer are placed one past whichever of their dependencies
 *    did get a depth, so the picture is wrong but every node is on it and the
 *    fold still terminates.
 */
export function layoutGraph(tasks: readonly GraphNodeInput[]): GraphLayout {
  const keys: string[] = [];
  const known = new Set<string>();
  const deps = new Map<string, string[]>();
  const dependents = new Map<string, string[]>();
  for (const task of tasks) {
    if (known.has(task.key)) continue;
    known.add(task.key);
    keys.push(task.key);
    deps.set(task.key, []);
    dependents.set(task.key, []);
  }

  const edges: PlacedEdge[] = [];
  const placed = new Set<string>();
  for (const task of tasks) {
    if (placed.has(task.key)) continue;
    placed.add(task.key);
    const own = deps.get(task.key)!;
    for (const dep of task.dependsOn) {
      if (dep === task.key || !known.has(dep) || own.includes(dep)) continue;
      own.push(dep);
      dependents.get(dep)!.push(task.key);
      edges.push({ from: dep, to: task.key });
    }
  }

  // Longest path from the roots, by Kahn: a node is layered only once every
  // dependency of it has been, and it lands one past the deepest of them.
  const depth = new Map<string, number>();
  const indegree = new Map(keys.map((key) => [key, deps.get(key)!.length]));
  const queue = keys.filter((key) => indegree.get(key) === 0);
  for (const key of queue) depth.set(key, 0);
  for (let index = 0; index < queue.length; index++) {
    const key = queue[index]!;
    const here = depth.get(key)!;
    for (const next of dependents.get(key)!) {
      depth.set(next, Math.max(depth.get(next) ?? 0, here + 1));
      const left = indegree.get(next)! - 1;
      indegree.set(next, left);
      if (left === 0) queue.push(next);
    }
  }
  for (const key of keys) {
    if (depth.has(key)) continue;
    const layered = deps
      .get(key)!
      .map((dep) => depth.get(dep))
      .filter((value): value is number => value !== undefined);
    depth.set(key, layered.length ? Math.max(...layered) + 1 : 0);
  }

  const byColumn = new Map<number, string[]>();
  for (const key of keys) {
    const column = depth.get(key)!;
    const bucket = byColumn.get(column);
    if (bucket) bucket.push(key);
    else byColumn.set(column, [key]);
  }
  for (const bucket of byColumn.values()) bucket.sort(compareKeys);

  const columns = keys.length ? Math.max(...byColumn.keys()) + 1 : 0;
  const rows = keys.length ? Math.max(...[...byColumn.values()].map((one) => one.length)) : 0;

  const nodes: PlacedNode[] = [];
  for (let column = 0; column < columns; column++) {
    const bucket = byColumn.get(column) ?? [];
    // Columns are centred against the tallest one, so a diamond reads as a
    // diamond rather than as a staircase hanging off the top edge.
    const offset = Math.round(((rows - bucket.length) / 2) * ROW_PITCH);
    bucket.forEach((key, row) => {
      nodes.push({
        key,
        depth: column,
        row,
        x: column * COLUMN_PITCH,
        y: offset + row * ROW_PITCH,
      });
    });
  }

  return {
    nodes,
    edges,
    columns,
    rows,
    width: columns ? columns * COLUMN_PITCH - COLUMN_GAP : 0,
    height: rows ? rows * ROW_PITCH - ROW_GAP : 0,
  };
}

/**
 * One edge, as an orthogonal path: out of the right edge of the dependency,
 * along its own row, one turn in the gutter immediately before the target, and
 * into its left edge.
 *
 * Turning late rather than early is what keeps a long edge readable. An edge
 * spanning three columns that turned in the first gutter would run vertically
 * through the middle of two columns of nodes; turning in the last one means it
 * only ever passes *behind* the cards on its own row, and the cards are opaque
 * and drawn over the lines.
 */
export function edgePath(from: PlacedNode, to: PlacedNode): string {
  const x1 = from.x + NODE_WIDTH;
  const y1 = from.y + NODE_HEIGHT / 2;
  const x2 = to.x;
  const y2 = to.y + NODE_HEIGHT / 2;
  if (y1 === y2) return `M ${x1} ${y1} H ${x2}`;
  const turn = x2 - COLUMN_GAP / 2;
  return `M ${x1} ${y1} H ${turn} V ${y2} H ${x2}`;
}
